import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Receipt, RefreshCw, Truck, ShoppingCart } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const R = (n) => 'R ' + Number(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function AllPayslipsPage() {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const today = new Date().toISOString().slice(0, 7);
  const [month, setMonth] = useState(today);
  const [data, setData] = useState({ rows: [], count: 0, transport_count: 0, spares_count: 0, total_gross: 0, total_net: 0 });
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [issuingSpares, setIssuingSpares] = useState(false);

  const fetchPayslips = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/payslips/consolidated`, { headers, params: { month } });
      setData(res.data || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load payslips');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  useEffect(() => { fetchPayslips(); }, [fetchPayslips]);

  const handleSyncToExpenses = async () => {
    setSyncing(true);
    try {
      const r = await axios.post(`${API_URL}/transport-expenses/sync-monthly-totals`, null, { headers, params: { month } });
      const inj = r.data.spares_injection_total || 0;
      toast.success(
        `Synced ${month}: Salaries R${r.data.payslip_total.toFixed(2)} · Fuel R${r.data.fuel_total.toFixed(2)}` +
        (inj > 0 ? ` (cash R${r.data.fuel_cash_total.toFixed(2)} + spares injections R${inj.toFixed(2)})` : '') +
        ' → JSM Transport Expenses'
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleIssueAllSpares = async () => {
    if (!window.confirm(`Issue payslips for ALL Spares Salesmen for ${month}? Salesmen already issued will be skipped.`)) return;
    setIssuingSpares(true);
    try {
      const r = await axios.post(`${API_URL}/auto-spares/admin/issue-all-payslips`, { period: month }, { headers });
      const d = r.data;
      toast.success(`${month}: ${d.issued_count} spares payslip(s) issued, ${d.skipped_count} already on file`);
      await fetchPayslips();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to issue spares payslips');
    } finally {
      setIssuingSpares(false);
    }
  };

  const transportRows = data.rows.filter(r => r.kind === 'transport');
  const sparesRows = data.rows.filter(r => r.kind === 'spares');

  const KindBadge = ({ kind }) => kind === 'transport' ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-800"><Truck className="h-3 w-3" /> TRANSPORT</span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800"><ShoppingCart className="h-3 w-3" /> SPARES</span>
  );

  return (
    <DashboardLayout>
      <div className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Receipt className="h-6 w-6 text-violet-600" /> All Payslips (Transport + Spares)
            </h1>
            <p className="text-sm text-muted-foreground">
              Combined view of every issued payslip. Use <b>Sync to Transport Expenses</b> to push the gross-pay total + actual fuel into JSM Transport Expenses for the month.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="w-44"
              data-testid="all-payslips-month"
            />
            <Button
              onClick={handleIssueAllSpares}
              disabled={issuingSpares}
              variant="outline"
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
              data-testid="issue-all-spares-btn"
            >
              <ShoppingCart className={`h-4 w-4 mr-1 ${issuingSpares ? 'animate-spin' : ''}`} />
              {issuingSpares ? 'Issuing…' : 'Issue Spares Payslips'}
            </Button>
            <Button
              onClick={handleSyncToExpenses}
              disabled={syncing}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="sync-to-expenses-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing…' : 'Sync to Transport Expenses'}
            </Button>
          </div>
        </div>

        {/* Hint: spares haven't been issued yet for this month */}
        {!loading && data.spares_count === 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-900 flex items-start gap-2">
            <ShoppingCart className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <div>
              <b>No Spares Salesmen payslips for {month}.</b> Unlike Transport (auto-saved), Spares payslips need to be issued explicitly.
              Click <b>Issue Spares Payslips</b> above to generate them for every salesman based on their {month} sales, fuel toggle, commission, and any deductions.
            </div>
          </div>
        )}

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <div className="text-[11px] uppercase tracking-wide text-blue-700 font-semibold">Transport Staff</div>
              <div className="text-2xl font-bold text-blue-900 mt-1" data-testid="transport-count">{data.transport_count}</div>
              <div className="text-xs text-blue-700 mt-1">
                Gross: <b>{R(transportRows.reduce((s, r) => s + r.gross_pay, 0))}</b>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-4">
              <div className="text-[11px] uppercase tracking-wide text-amber-700 font-semibold">Spares Salesmen</div>
              <div className="text-2xl font-bold text-amber-900 mt-1" data-testid="spares-count">{data.spares_count}</div>
              <div className="text-xs text-amber-700 mt-1">
                Gross: <b>{R(sparesRows.reduce((s, r) => s + r.gross_pay, 0))}</b>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-violet-50 border-violet-200">
            <CardContent className="p-4">
              <div className="text-[11px] uppercase tracking-wide text-violet-700 font-semibold">Total Gross</div>
              <div className="text-2xl font-bold text-violet-900 mt-1" data-testid="total-gross">{R(data.total_gross)}</div>
              <div className="text-xs text-violet-700 mt-1">{data.count} payslip{data.count === 1 ? '' : 's'}</div>
            </CardContent>
          </Card>
          <Card className="bg-emerald-50 border-emerald-200">
            <CardContent className="p-4">
              <div className="text-[11px] uppercase tracking-wide text-emerald-700 font-semibold">Total Net</div>
              <div className="text-2xl font-bold text-emerald-900 mt-1" data-testid="total-net">{R(data.total_net)}</div>
              <div className="text-xs text-emerald-700 mt-1">After loan + deductions</div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Payslips · {month}</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {loading ? (
              <div className="text-center py-10 text-muted-foreground">Loading…</div>
            ) : data.rows.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground">
                No payslips have been issued for {month}.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-24">Type</TableHead>
                    <TableHead className="w-28">Month</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead className="text-right">Basic</TableHead>
                    <TableHead className="text-right">Gross</TableHead>
                    <TableHead className="text-right">Net</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody data-testid="all-payslips-tbody">
                  {data.rows.map((r) => (
                    <TableRow key={`${r.kind}-${r.id}`} data-testid={`payslip-row-${r.id}`}>
                      <TableCell><KindBadge kind={r.kind} /></TableCell>
                      <TableCell className="font-mono text-xs">{r.month}</TableCell>
                      <TableCell className="font-medium">{r.name}</TableCell>
                      <TableCell className="text-right font-mono text-xs text-muted-foreground">{R(r.basic_salary)}</TableCell>
                      <TableCell className="text-right font-mono font-semibold">{R(r.gross_pay)}</TableCell>
                      <TableCell className="text-right font-mono font-semibold text-emerald-700">{R(r.net_pay)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-violet-50 font-bold">
                    <TableCell colSpan={3} className="text-right">TOTAL</TableCell>
                    <TableCell className="text-right font-mono text-xs">—</TableCell>
                    <TableCell className="text-right font-mono">{R(data.total_gross)}</TableCell>
                    <TableCell className="text-right font-mono text-emerald-800">{R(data.total_net)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
